// React Imports
import { useEffect, useRef, useState } from 'react'

// MUI Imports
import IconButton from '@mui/material/IconButton'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'

// Hook Imports
import { useSettings } from '@core/hooks/useSettings'

type LanguageDataType = {
  langCode: 'en' | 'ar'
  langName: string
  dir: 'ltr' | 'rtl'
}

const languageData: LanguageDataType[] = [
  { langCode: 'en', langName: 'English', dir: 'ltr' },
  { langCode: 'ar', langName: 'العربية', dir: 'rtl' }
]

const STORAGE_KEY = 'app-locale'

const applyLocale = (langCode: 'en' | 'ar', dir: 'ltr' | 'rtl') => {
  document.documentElement.lang = langCode
  document.documentElement.dir = dir

  try {
    localStorage.setItem(STORAGE_KEY, langCode)
  } catch {
    /* noop */
  }
}

const LanguageDropdown = () => {
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<'en' | 'ar'>('en')
  const anchorRef = useRef<HTMLButtonElement>(null)

  const { settings } = useSettings()

  useEffect(() => {
    let stored: 'en' | 'ar' | null = null

    try {
      stored = (localStorage.getItem(STORAGE_KEY) as 'en' | 'ar' | null) || null
    } catch {
      stored = null
    }

    const lang: 'en' | 'ar' = stored === 'ar' ? 'ar' : 'en'
    const dir: 'ltr' | 'rtl' = lang === 'ar' ? 'rtl' : 'ltr'

    applyLocale(lang, dir)
    setCurrent(lang)
  }, [])

  const handleClose = () => setOpen(false)
  const handleToggle = () => setOpen(prev => !prev)

  const handleSelect = (locale: LanguageDataType) => {
    if (locale.langCode === current) {
      setOpen(false)

      return
    }

    applyLocale(locale.langCode, locale.dir)
    setCurrent(locale.langCode)
    setOpen(false)

    // Reload so DashboardLayout/menus re-render with the new dictionary
    window.location.reload()
  }

  return (
    <>
      <IconButton ref={anchorRef} onClick={handleToggle} className='text-textPrimary'>
        <i className='tabler-language' />
      </IconButton>
      <Popper
        open={open}
        transition
        disablePortal
        placement='bottom-start'
        anchorEl={anchorRef.current}
        className='min-is-[160px] !mbs-3 z-[1]'
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'right top' }}
          >
            <Paper className={settings.skin === 'bordered' ? 'border shadow-none' : 'shadow-lg'}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList onKeyDown={handleClose}>
                  {languageData.map(locale => (
                    <MenuItem
                      key={locale.langCode}
                      onClick={() => handleSelect(locale)}
                      selected={current === locale.langCode}
                    >
                      {locale.langName}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default LanguageDropdown
